import { createContext, useContext, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { nestedLocationsApi } from '../../../axios/nestedLocations';
import { useDebounce, useFeatureFlags, useLanguage } from '../../../hooks';
import { NestedLocation, NestedLocationResponse } from '../../../types/nestedLocations';
import { buildLocationIndexes } from '../utils/LocationsHelpers';
import {
  MAX_CHILD_LOCATIONS_PER_PAGE,
  MAX_ROOT_LOCATIONS_PER_PAGE,
} from '../utils/LocationsParameters';
import { SelectedStatus, useLocationsReducer } from '../utils/LocationsReducer';

interface NestedLocationsContextType {
  locationsState: ReturnType<typeof useLocationsReducer>[0];
  locationsDispatch: ReturnType<typeof useLocationsReducer>[1];
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  debouncedSearchValue: string;
  setDebouncedSearchValue: React.Dispatch<React.SetStateAction<string>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalLocations: number;
  isLoadingLocationsTree: boolean;
  numberOfPages: number;
  handleExpandLocation: (locationId: number) => void;
  handleLoadMore: (parentLocation: NestedLocation) => void;
  handleSearch: (query: string) => void;
  hasNestableLocations: boolean;
  hasOldLocations: boolean;
  numberOfNestableLocations: number;
  isLoadingAccountStatusWithLocations: boolean;
  loadingChildren: Record<number, boolean>;
  maxDepth: number;
  maxNestedLocationsPerParent: number | null;
  maxNumberOfLocations: number | null;
}

export const NestedLocationsContext = createContext<NestedLocationsContextType>({
  locationsState: {
    locationById: new Map<number, NestedLocation>(),
    childrenByParentId: new Map<number, number[]>(),
    selectedLocations: new Map<number, SelectedStatus>(),
    allLocationsSelected: false,
  },
  locationsDispatch: () => undefined,
  searchQuery: '',
  setSearchQuery: () => '',
  debouncedSearchValue: '',
  setDebouncedSearchValue: () => '',
  currentPage: 1,
  setCurrentPage: () => 1,
  totalLocations: 0,
  isLoadingLocationsTree: false,
  numberOfPages: 0,
  handleExpandLocation: () => undefined,
  handleLoadMore: () => undefined,
  handleSearch: () => undefined,
  hasNestableLocations: false,
  hasOldLocations: false,
  numberOfNestableLocations: 0,
  isLoadingAccountStatusWithLocations: true,
  loadingChildren: {},
  maxDepth: 0,
  maxNestedLocationsPerParent: 0,
  maxNumberOfLocations: 0,
});

const getChildrenLocationsFromBackend = (locationId: number, offset = 0) =>
  nestedLocationsApi.getNestedLocations({
    parent_id: locationId,
    only_children: true,
    limit: MAX_CHILD_LOCATIONS_PER_PAGE,
    offset,
  });

const initialLocationsResponse: NestedLocationResponse = {
  locations: [],
  next_page_exists: false,
  prev_page_exists: false,
  total_locations: 0,
};

export const NestedLocationsProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [locationsState, locationsDispatch] = useLocationsReducer();
  const { enqueueSnackbar } = useSnackbar();
  const lang = useLanguage();

  // Record of locations that are currently loading children.
  // {locationId: number => isLoading: boolean}
  const [loadingChildren, setLoadingChildren] = useState<Record<number, boolean>>({});

  const debouncedSearch = useDebounce(() => {
    if (searchQuery.length > 1 || searchQuery === '') {
      setDebouncedSearchValue(searchQuery);
    }
  }, 300);

  const { getPremiumFeature } = useFeatureFlags();
  const maxDepth = parseInt(getPremiumFeature('max_depth_nestable_location') || '1');
  const maxNumberOfLocations = parseInt(getPremiumFeature('max_nestable_locations') || '0');
  const maxNestedLocationsPerParent = parseInt(getPremiumFeature('max_nested_locations') || '0');

  const {
    data: rootLocationsResponse = initialLocationsResponse,
    isLoading: isLoadingLocationsTree,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['locationsIndex', debouncedSearchValue, currentPage],
    queryFn: () =>
      nestedLocationsApi.getNestedLocations({
        search: debouncedSearchValue,
        only_root: !debouncedSearchValue,
        limit: MAX_ROOT_LOCATIONS_PER_PAGE,
        offset: (currentPage - 1) * MAX_ROOT_LOCATIONS_PER_PAGE,
      }),
  });

  const handleExpandLocation = async (locationId: number) => {
    locationsDispatch({ type: 'set_location_expanded', payload: { locationId } });
    if (loadingChildren[locationId]) return;
    if (locationsState.childrenByParentId.get(locationId)?.length > 0) return;

    setLoadingChildren((prev) => ({ ...prev, [locationId]: true }));

    try {
      const locationsResponse = await getChildrenLocationsFromBackend(locationId);
      locationsDispatch({
        type: 'update_location_children',
        payload: {
          targetLocationId: locationId,
          newLocations: locationsResponse.locations,
          has_more_children: locationsResponse.next_page_exists,
        },
      });
    } catch (error) {
      enqueueSnackbar(lang.locations.table.loadMoreError, { variant: 'error' });
    }

    setLoadingChildren((prev) => ({ ...prev, [locationId]: false }));
  };

  const handleLoadMore = async (parentLocation: NestedLocation) => {
    if (loadingChildren[parentLocation.id]) return;
    const currentChildren = locationsState.childrenByParentId.get(parentLocation.id) || [];

    setLoadingChildren((prev) => ({ ...prev, [parentLocation.id]: true }));

    try {
      const locationsResponse = await getChildrenLocationsFromBackend(
        parentLocation.id,
        currentChildren.length,
      );

      locationsDispatch({
        type: 'update_location_children',
        payload: {
          targetLocationId: parentLocation.id,
          newLocations: locationsResponse.locations,
          has_more_children: locationsResponse.next_page_exists,
        },
      });
    } catch (error) {
      enqueueSnackbar(lang.locations.table.loadMoreError, { variant: 'error' });
    }

    setLoadingChildren((prev) => ({ ...prev, [parentLocation.id]: false }));
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    debouncedSearch();
    setCurrentPage(1);
  };

  const { data: accountStatusWithLocations, isPending: isLoadingAccountStatusWithLocations } =
    useQuery({
      queryKey: ['accountStatusWithLocations'],
      queryFn: () => nestedLocationsApi.checkAccountStatusWithLocations(),
    });

  const hasNestableLocations = accountStatusWithLocations?.has_nestable_locations ?? false;
  const hasOldLocations = accountStatusWithLocations?.has_old_locations ?? false;
  const numberOfNestableLocations = accountStatusWithLocations?.number_of_nestable_locations ?? 0;

  useEffect(() => {
    if (dataUpdatedAt > 0) {
      const locationIndexes = buildLocationIndexes(rootLocationsResponse.locations);
      locationsDispatch({
        type: 'reset_location_state',
        payload: locationIndexes,
      });
      setLoadingChildren({});
    }
  }, [dataUpdatedAt]);

  const numberOfPages = Math.ceil(
    rootLocationsResponse.total_locations / MAX_ROOT_LOCATIONS_PER_PAGE,
  );

  return (
    <NestedLocationsContext.Provider
      value={{
        locationsState,
        locationsDispatch,
        searchQuery,
        setSearchQuery,
        debouncedSearchValue,
        setDebouncedSearchValue,
        currentPage,
        setCurrentPage,
        totalLocations: rootLocationsResponse.total_locations,
        isLoadingLocationsTree,
        numberOfPages,
        handleExpandLocation,
        handleLoadMore,
        handleSearch,
        hasNestableLocations,
        hasOldLocations,
        numberOfNestableLocations,
        isLoadingAccountStatusWithLocations,
        loadingChildren,
        maxDepth,
        maxNestedLocationsPerParent,
        maxNumberOfLocations,
      }}
    >
      {children}
    </NestedLocationsContext.Provider>
  );
};

export const useNestedLocationsContext = () => {
  const context = useContext(NestedLocationsContext);

  if (context === undefined) {
    throw new Error('useNestedLocationsContext must be used within an NestedLocationsProvider');
  }
  return context;
};
